import { DataAccess } from "../../../dataaccess/data.access";
import { Cache, Invalidate } from "../../../decorators/cache.decorator";
import { Timeout } from "../../../decorators/timeout.decorator";
import { S25Util } from "../../../util/s25-util";
import { Budget } from "./Budget";
import { BudgetPlanService } from "./budget-plan/budget.plan.service";

const SERVICE = "BudgetService";

export type BudgetIncludeOption = "plans" | "transactions" | "categories" | "events" | "lineItems" | "all";

export class BudgetService {
    // ============================================================================
    // ORGANIZATION BUDGET OPERATIONS
    // ============================================================================

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getOrgBudget(organizationId: number): Promise<Budget.OrgBudgetResponse> {
        return DataAccess.get<Budget.OrgBudgetResponse>(
            DataAccess.injectCaller(`/budget/org.json?organizationId=${organizationId}`, SERVICE + ".getOrgBudget"),
        );
    }

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getOrgBudgetWithIncludes(
        organizationId: number,
        includes: BudgetIncludeOption[],
    ): Promise<Budget.OrgBudgetResponse> {
        const includeStr = includes.length > 0 ? `&include=${includes.join("+")}` : "";
        return DataAccess.get<Budget.OrgBudgetResponse>(
            DataAccess.injectCaller(
                `/budget/org.json?scope=extended&organizationId=${organizationId}${includeStr}`,
                SERVICE + ".getOrgBudgetWithIncludes",
            ),
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static createOrgBudget(
        organizationId: number,
        payload: Budget.OrgBudget,
    ): Promise<Budget.OrgBudgetResponse> {
        payload.currency = payload.currency || "usd";
        return DataAccess.post<Budget.OrgBudgetResponse>(
            DataAccess.injectCaller(`/budget/org.json?organizationId=${organizationId}`, SERVICE + ".createOrgBudget"),
            payload,
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static updateOrgBudget(
        organizationId: number,
        payload: Partial<Budget.OrgBudget>,
    ): Promise<Budget.OrgBudgetResponse> {
        return DataAccess.put<Budget.OrgBudgetResponse>(
            DataAccess.injectCaller(`/budget/org.json?organizationId=${organizationId}`, SERVICE + ".updateOrgBudget"),
            payload,
        );
    }

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getBudgetSummary(organizationId: number): Promise<Budget.BudgetSummary> {
        return DataAccess.get<Budget.BudgetSummary>(
            DataAccess.injectCaller(
                `/budget/summary.json?organizationId=${organizationId}`,
                SERVICE + ".getBudgetSummary",
            ),
        );
    }

    @Cache({ targetName: "BudgetService", immutable: true })
    public static async getAllOrgBudgets(organizationId: number): Promise<Budget.OrgBudget[]> {
        return DataAccess.get<Budget.OrgBudget[]>(
            DataAccess.injectCaller(
                `/budget/orgs.json?organizationId=${organizationId}`,
                SERVICE + ".getAllOrgBudgets",
            ),
        ).then((data) => data || []);
    }

    // ============================================================================
    // BUDGET LINE ITEM OPERATIONS
    // ============================================================================

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getBudgetLineItems(budgetPlanId: number): Promise<Budget.BudgetLineItem[]> {
        return DataAccess.get<Budget.BudgetLineItem[]>(
            DataAccess.injectCaller(
                `/budget/line_items.json?budgetPlanId=${budgetPlanId}`,
                SERVICE + ".getBudgetLineItems",
            ),
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static createBudgetLineItem(payload: Budget.BudgetLineItem): Promise<Budget.BudgetLineItem> {
        return DataAccess.post<Budget.BudgetLineItem>(
            DataAccess.injectCaller(`/budget/line_item.json`, SERVICE + ".createBudgetLineItem"),
            payload,
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static updateBudgetLineItem(
        lineItemId: number,
        payload: Partial<Budget.BudgetLineItem>,
    ): Promise<Budget.BudgetLineItem> {
        if (!lineItemId) {
            const newPayload: Budget.BudgetLineItem = {
                ...payload,
                lineItemId: undefined,
                actualAmount: payload.actualAmount ?? 0,
                amount: payload.amount ?? 0,
                budgetId: payload.budgetId ?? 0,
                budgetPlanId: payload.budgetPlanId ?? 0,
                categoryId: undefined, //TODO support budget categories
                categoryName: undefined, //TODO support budget categories
                committedAmount: payload.committedAmount ?? 0,
                createUser: payload.createUser ?? "",
                createDate: payload.createDate ?? undefined,
                currency: payload.currency ?? "",
                lastModUser: payload.lastModUser ?? "",
                lastModDate: payload.lastModDate ?? undefined,
                name: payload.name ?? "",
                plannedAmount: payload.plannedAmount ?? 0,
                transactionIds: payload.transactionIds ?? [],
            };

            return BudgetService.createBudgetLineItem(newPayload);
        }

        return DataAccess.put<Budget.BudgetLineItem>(
            DataAccess.injectCaller(
                `/budget/line_item.json?lineItemId=${lineItemId}`,
                SERVICE + ".updateBudgetLineItem",
            ),
            payload,
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static deleteBudgetLineItem(lineItemId: number): Promise<{ success: boolean }> {
        return DataAccess.delete<{ success: boolean }>(
            DataAccess.injectCaller(
                `/budget/line_item.json?lineItemId=${lineItemId}`,
                SERVICE + ".deleteBudgetLineItem",
            ),
        );
    }

    // ============================================================================
    // BUDGET CATEGORY OPERATIONS
    // ============================================================================

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getBudgetCategories(organizationId?: number): Promise<Budget.BudgetCategory[]> {
        return DataAccess.get<Budget.BudgetCategory[]>(
            DataAccess.injectCaller("/budget/categories.json", SERVICE + ".getBudgetCategories"),
        ).then((data) => data || []);
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static createBudgetCategory(payload: Budget.BudgetCategory): Promise<Budget.BudgetCategory> {
        return DataAccess.post<Budget.BudgetCategory>(
            DataAccess.injectCaller(`/budget/category.json`, SERVICE + ".createBudgetCategory"),
            payload,
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static updateBudgetCategory(
        categoryId: number,
        payload: Partial<Budget.BudgetCategory>,
    ): Promise<Budget.BudgetCategory> {
        return DataAccess.put<Budget.BudgetCategory>(
            DataAccess.injectCaller(
                `/budget/category.json?categoryId=${categoryId}`,
                SERVICE + ".updateBudgetCategory",
            ),
            payload,
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static deleteBudgetCategory(categoryId: number): Promise<{ success: boolean }> {
        return DataAccess.delete<{ success: boolean }>(
            DataAccess.injectCaller(
                `/budget/category.json?categoryId=${categoryId}`,
                SERVICE + ".deleteBudgetCategory",
            ),
        );
    }

    // ============================================================================
    // ADMIN OPERATIONS
    // ============================================================================
    @Cache({ targetName: "BudgetService", immutable: true })
    public static getAdminBudgetSummary(fiscalYear: string): Promise<Budget.AdminBudgetSummary> {
        return DataAccess.get<Budget.AdminBudgetSummary>(
            DataAccess.injectCaller(
                `/budget/admin/summary.json?fiscalYear=${fiscalYear}`,
                SERVICE + ".getAdminBudgetSummary",
            ),
        );
    }

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getOrganizationBudgetStatus(fiscalYear: string): Promise<Budget.OrganizationBudgetStatus[]> {
        return DataAccess.get<{ organizations: Budget.OrganizationBudgetStatus[] }>(
            DataAccess.injectCaller(
                `/budget/admin/organizations.json?fiscalYear=${fiscalYear}`,
                SERVICE + ".getOrganizationBudgetStatus",
            ),
        ).then((data) => data?.organizations || []);
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    public static async getBudgetUtilization(organizationId: number): Promise<{
        totalAllocated: number;
        totalSpent: number;
        totalCommitted: number;
        remainingBalance: number;
        utilizationPercentage: number;
    }> {
        const summary = await BudgetService.getBudgetSummary(organizationId);
        return {
            totalAllocated: summary.totalAllocated,
            totalSpent: summary.totalSpent,
            totalCommitted: summary.totalCommitted,
            remainingBalance: summary.remainingBalance,
            utilizationPercentage: summary.utilizationPercentage,
        };
    }

    public static async validateBudgetAmounts(
        budgetPlanId: number,
        newAmount: number,
    ): Promise<{ isValid: boolean; message?: string }> {
        try {
            const budgetPlan = await BudgetPlanService.getBudgetPlan(budgetPlanId);
            const orgBudget = budgetPlan.orgBudget;

            const totalCommitted = orgBudget.totalCommitted + newAmount;
            const isValid = totalCommitted <= orgBudget.totalAllocated;

            return {
                isValid,
                message: isValid ? undefined : "Amount exceeds available budget allocation",
            };
        } catch (error) {
            return {
                isValid: false,
                message: "Unable to validate budget amounts",
            };
        }
    }

    // ============================================================================
    // FISCAL YEAR OPERATIONS
    // ============================================================================

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getFiscalYears(): Promise<Budget.FiscalYear[]> {
        return DataAccess.get<Budget.FiscalYear[]>(
            DataAccess.injectCaller("/budget/fiscal_years.json", SERVICE + ".getFiscalYears"),
        ).then((data) => data || []);
    }

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getFiscalYear(fiscalYearId: number): Promise<Budget.FiscalYear> {
        return DataAccess.get<Budget.FiscalYear>(
            DataAccess.injectCaller(
                `/budget/fiscal_year.json?fiscalYearId=${fiscalYearId}`,
                SERVICE + ".getFiscalYear",
            ),
        );
    }

    @Cache({ targetName: "BudgetService", immutable: true })
    public static getCurrentFiscalYear(): Promise<Budget.FiscalYear> {
        return DataAccess.get<Budget.FiscalYear>(
            DataAccess.injectCaller("/budget/fiscal_year/current.json", SERVICE + ".getCurrentFiscalYear"),
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static createFiscalYear(fiscalYear: Budget.FiscalYear): Promise<any> {
        return DataAccess.post(
            DataAccess.injectCaller("/budget/fiscal_year.json", SERVICE + ".createFiscalYear"),
            fiscalYear,
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static updateFiscalYear(fiscalYearId: number, fiscalYear: Budget.FiscalYear): Promise<any> {
        return DataAccess.put(
            DataAccess.injectCaller(
                `/budget/fiscal_year.json?fiscalYearId=${fiscalYearId}`,
                SERVICE + ".updateFiscalYear",
            ),
            fiscalYear,
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static deleteFiscalYear(fiscalYearId: number): Promise<any> {
        return DataAccess.delete(
            DataAccess.injectCaller(
                `/budget/fiscal_year.json?fiscalYearId=${fiscalYearId}`,
                SERVICE + ".deleteFiscalYear",
            ),
        );
    }

    @Invalidate({ serviceName: "BudgetService" })
    public static setCurrentFiscalYear(fiscalYearId: number): Promise<any> {
        return DataAccess.put(
            DataAccess.injectCaller(
                `/budget/fiscal_year/set_current.json?fiscalYearId=${fiscalYearId}`,
                SERVICE + ".setCurrentFiscalYear",
            ),
            {},
        );
    }
}
